import React, { useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import excelIcon from "../../../assets/images/SBA/excel_icon.png";
import { exportToExcel } from "../../../shared/utils/exportToExcel";
import useDocumentTitle from "../../../shared/customHooks/useDocumentTitle";
import {
  getActivityHistory,
  getActivityHistoryUsers,
  getActivityHistoryContacts,
  getContactInfo,
} from "../service/adminContact_service";

const ActivityHistoryTab = ({ contactId: contactIdProp = null }) => {
  useDocumentTitle("Activity History");
  const getContactIdFromUrl = () => {
    const path = window.location.pathname || "";
    const m = path.match(/\/AdminContact\/(\d+)/i);
    return m ? m[1] : "";
  };

  const effectiveContactId = String(contactIdProp ?? getContactIdFromUrl() ?? "").trim();

  const toInputDate = (date) => {
    const d = new Date(date);
    const mm = `${d.getMonth() + 1}`.padStart(2, "0");
    const dd = `${d.getDate()}`.padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  };

  const toMvcDate = (inputDate) => {
    if (!inputDate) return null;
    const [yyyy, mm, dd] = inputDate.split("-");
    return `${mm}/${dd}/${yyyy}`;
  };

  const today = new Date();
  const fromDefault = new Date();
  fromDefault.setDate(fromDefault.getDate() - 30);

  const [fromDate, setFromDate] = useState(toInputDate(fromDefault));
  const [toDate, setToDate] = useState(toInputDate(today));
  const [pageSize] = useState(15);
  const [dropdownData, setDropdownData] = useState([
    { key: "none--1", id: "-1", name: "--Please Select--", type: "none" },
  ]);
  const [selectedKey, setSelectedKey] = useState("none--1");

  const [lockedMode, setLockedMode] = useState(false);
  const [lockedUserLabel, setLockedUserLabel] = useState("");
  const [lockedContactId, setLockedContactId] = useState("");

  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const selectedItem = useMemo(
    () => dropdownData.find((x) => x.key === selectedKey) || null,
    [dropdownData, selectedKey]
  );

  const columnDefs = useMemo(
    () => [
      { field: "userName", headerName: "Name",  flex: 1, sortable: true, filter: true , filter:'agTextColumnFilter'},
      { field: "pageUrl", headerName: "Url",  flex: 1, sortable: true, filter: true, filter:'agTextColumnFilter' },
      { field: "pageTitle", headerName: "Page", flex: 1, sortable: true, filter: true , filter:'agTextColumnFilter'},
      { field: "accessedIP", headerName: "Accessed IP",  sortable: true, filter: true , filter:'agTextColumnFilter'},
      { field: "sessionId", headerName: "Session Id",  sortable: true, filter: true, filter:'agTextColumnFilter' },
      { field: "browserUsed", headerName: "Browser",  flex: 1, sortable: true, filter: true , filter:'agTextColumnFilter'},
      { field: "accessedDateFilter", headerName: "Accessed Date", sortable: true, filter: true , filter:'agTextColumnFilter'},
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      floatingFilter: true,
     
    }),
    []
  );

  const loadGrid = async (item = selectedItem) => {
    try {
      setLoading(true);
      setErrorMsg("");

      let userId = 0;
      let contactId = 0;

      if (lockedMode && lockedContactId) {
        contactId = Number(lockedContactId) || 0;
      } else {
        if (item?.type === "user") userId = Number(item.id) || 0;
        if (item?.type === "contact") contactId = Number(item.id) || 0;
      }

      const res = await getActivityHistory(userId, contactId, toMvcDate(fromDate), toMvcDate(toDate));
      setRowData(res?.data || []);
    } catch {
      setRowData([]);
      setErrorMsg("Failed to load activity history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        setErrorMsg("");

       
        if (effectiveContactId) {
          const infoRes = await getContactInfo(effectiveContactId);
          const info = infoRes?.data?.[0] || {};

          const displayName = info.webUserName || "";
          const contactIdForGrid = Number(info.contactId || effectiveContactId || 0);

          setLockedUserLabel(`${displayName} [Contact]`);
          setLockedContactId(String(contactIdForGrid));
          setLockedMode(true);

          await loadGrid({ type: "contact", id: String(contactIdForGrid) });
          return;
        }

        
        const [usersRes, contactsRes] = await Promise.all([
          getActivityHistoryUsers(),
          getActivityHistoryContacts(),
        ]);

        const usersRaw = usersRes?.data || [];
        const contactsRaw = contactsRes?.data || [];

        const merged = [{ key: "none--1", id: "-1", name: "--Please Select--", type: "none" }];

        usersRaw.forEach((u, idx) => {
          const id = String(u?.userId ?? u?.UserId ?? "").trim();
          const name = u?.userName ?? u?.UserName ?? "";
          if (id && name) {
            merged.push({ key: `user-${id}-${idx}`, id, name: `${name} [User]`, type: "user" });
          }
        });

        contactsRaw.forEach((c, idx) => {
          const id = String(c?.webUserId ?? c?.WebUserId ?? "").trim();
          const name = c?.webUserName ?? c?.WebUserName ?? "";
          if (id && name) {
            merged.push({ key: `contact-${id}-${idx}`, id, name: `${name} [Contact]`, type: "contact" });
          }
        });

        setDropdownData(merged);
        setSelectedKey("none--1");
        setLockedMode(false);
      } catch {
        setErrorMsg("Failed to initialize activity history.");
      }
    };

    init();
   
  }, [effectiveContactId]);

  const handleExport = () => {
  const headers = [
    "Name",
    "Url",
    "Page",
    "Accessed IP",
    "Session Id",
    "Browser",
    "Accessed Date",
  ];

  const rows = (rowData || []).map((r) => ({
    Name: r?.userName ?? "",
    Url: r?.pageUrl ?? "",
    Page: r?.pageTitle ?? "",
    "Accessed IP": r?.accessedIP ?? "",
    "Session Id": r?.sessionId ?? "",
    Browser: r?.browserUsed ?? "",
    "Accessed Date": r?.accessedDateFilter ?? "",
  }));

  exportToExcel({
    fileName: "Activity History.xlsx",
    headers,
    rows,
  });
};


  return (
    <div>     
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 20,
        padding: "25px 15px",
        background: "#1b0f63",
        color: "white",
        flexWrap: "wrap",
        marginTop: 16,
        marginBottom: 12,
        
      }}
    >
      {/* User */}
      <div style={{ display: "flex", alignItems: "center", gap: 8  }}>
        <label style={{ marginBottom: 0 }}>User:</label>

        {lockedMode ? (
          <input
            type="text"
            value={lockedUserLabel}
            disabled
            style={{ width: 320, height: 34 , backgroundColor: '#c1d8e0c0',color: "#003f59", fontSize:'18px'}}
          />
        ) : (
          <select
            style={{ width: 320, height: 34 }}
            value={selectedKey}
            onChange={(e) => setSelectedKey(e.target.value)}
          >
            {dropdownData.map((item) => (
              <option key={item.key} value={item.key}>
                {item.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* From */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <label style={{ marginBottom: 0 }}>From:</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              height: 34,
              color: "#003f59",
              background: "#fff",
              border: "1px solid #ccc"
            }}
          />
      </div>

      {/* To */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <label style={{ marginBottom: 0 }}>To:</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              height: 34,
              color: "#003f59",
              background: "#fff",
              border: "1px solid #ccc"
            }}
          />
      </div>

      {/* Go button */}
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => loadGrid()}
        disabled={loading}
        style={{ height: 34, padding: "0 18px" }}
      >
        {loading ? "Loading..." : "Go"}
      </button>
    </div>
     
      {errorMsg ? <div style={{ color: "red", marginBottom: 8 }}>{errorMsg}</div> : null}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
  <button
    type="button"
    onClick={handleExport}
    title="Export to Excel"
    style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer" }}
  >
    <img
      src={excelIcon} 
      alt="Export"
      style={{ width: 28, height: 28 }}
    />
  </button>
</div>


      <div className="ag-theme-alpine" style={{ width: "100%", height: 356 }}>
        <AgGridReact
          theme={"legacy"}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={pageSize}
          paginationPageSizeSelector={[10, 15, 50, 100, 200]}
          suppressCellFocus
          overlayNoRowsTemplate="No Records Available"
        />
      </div>
    </div>
  );
};

export default ActivityHistoryTab;
